import { Grid, GridRow, ModalBottomButtons } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { LessonEditStatus, LessonItemFragment } from '../../../../types/planung-graphql-client-defs';
import { EpochBasicsForm } from '../../Forms/Basics/EpochBasicsForm';
import { LessonClassesForm } from '../../Forms/Classes/LessonClassesForm';
import { TimetableDurationAndCounter } from '../../Forms/DurationAndCounter/TimetableDurationAndCounter';
import { epochSchema } from '../../Forms/validation/schema';
import { LessonFormType, VersionLessonFormType } from '../../types';
import { useTimetableLessons } from '../hooks/useTimetableLessons';
import { useLessonStore } from '../TimetableVersionLessonProvider';

type TimetableVersionSubjectContainerLessonFormProps = {
  lessonUuid?: string | null;
  defaultInitialValues: Partial<LessonFormType>;
  onClose: () => void;
  versionUuid: string;
  onLessonEdited: (response: { error?: unknown; data?: LessonItemFragment }) => void;
  onLessonCreated: (response: { error?: unknown; data?: LessonItemFragment }) => void;
};

export const TimetableVersionSubjectContainerLessonForm = observer(
  ({
    lessonUuid,
    defaultInitialValues,
    onClose,
    versionUuid,
    onLessonCreated,
    onLessonEdited,
  }: TimetableVersionSubjectContainerLessonFormProps) => {
    const { t } = useTranslation();

    const schoolYear = useSelectedSchoolYear();

    const [loading, setLoading] = useState<boolean>(true);

    const lessonStore = useLessonStore();
    const currentLessonFromStore = lessonUuid ? lessonStore.lessons.get(lessonUuid) : null;

    const { createLesson, updateLesson } = useTimetableLessons({
      schoolYearUuid: schoolYear?.uuid ?? '',
      versionUuid: versionUuid,
    });

    const [currentLessonFromDB, setCurrentLessonFromDB] = useState<LessonItemFragment | null>(null);

    useEffect(() => {
      if (lessonUuid) {
        lessonStore.lessonRequest({ uuids: [lessonUuid] }).then((response) => {
          if (response) {
            setCurrentLessonFromDB(response[0]);
          }
        });
        setLoading(false);
      } else {
        setLoading(false);
      }
    }, [lessonStore, lessonUuid]);

    const { subjectContainerData } = useLoadBasicData({ pause: loading });

    const validGrades = subjectContainerData?.subjectContainers.find(
      (sc) => sc.uuid === currentLessonFromStore?.subjectUuid,
    )?.grades;

    const status = currentLessonFromStore?.editStatus;
    const initialValues: VersionLessonFormType = currentLessonFromDB
      ? {
          uuid: currentLessonFromDB.uuid,
          comment: currentLessonFromDB.comment,
          lessonClasses: currentLessonFromDB.lessonClasses.map((lc) => {
            return { uuid: lc.uuid, class: lc.class, groups: lc.groups };
          }),
          validGrades: validGrades,
          subject: currentLessonFromDB.subject,
          subTitle: currentLessonFromDB.subTitle,
          durationCountArray: [
            {
              duration: 1,
              count: 1,
            },
          ],
        }
      : { ...defaultInitialValues, timetableEnabled: true, teachingLoadEnabled: true };

    const handleSubmit = async (values: VersionLessonFormType, formikHelpers: FormikHelpers<VersionLessonFormType>) => {
      setLoading(true);

      if (!values.uuid) {
        const createResult = await createLesson(values);
        onLessonCreated({ error: createResult.error, data: createResult.data });
        setLoading(false);
        formikHelpers.resetForm();
      } else {
        const updateResult = await updateLesson(values);
        onLessonEdited({ error: updateResult.error, data: updateResult.data });
        setLoading(false);
        formikHelpers.resetForm();
      }
    };

    return (
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={epochSchema}
      >
        {({ dirty, resetForm, errors, values }) => {
          // Filter out classes that are already used in subject container lessons
          const usedClassesPerSubjectContainer: string[] = Array.from(lessonStore.lessons.values())
            .filter((ld) => {
              if (ld.isSubjectContainerLesson && values?.subject?.uuid === ld.subjectUuid) {
                return true;
              }
              return false;
            })
            //filter classes that are in values.lessonClasses.map((lc) => lc.class.uuid) for initial values on edit
            .flatMap((l) =>
              l.classesUuids.filter((cUuid) => {
                return !values.lessonClasses?.some((lc) => lc.class.uuid === cUuid);
              }),
            );
          return (
            <Form>
              <Grid useFormGap>
                <GridRow>
                  <EpochBasicsForm status={status} preFilterSubjectContainers={false} />
                </GridRow>
                <GridRow headline={t('classes.title', { count: 2 })}>
                  <LessonClassesForm
                    disabled={status === LessonEditStatus.Blocked || !values.subject?.uuid}
                    filter={(c) => {
                      return c.grade
                        ? (values.validGrades?.includes(c.grade) ?? true) &&
                            !usedClassesPerSubjectContainer.includes(c.uuid)
                        : false;
                    }}
                  />
                  <div>{t('')}</div>
                </GridRow>

                {!lessonUuid && (
                  <GridRow headline={t('badgeCard.title', { count: 2 })}>
                    <TimetableDurationAndCounter
                      disabled={status === LessonEditStatus.Blocked || !values.lessonClasses}
                    />
                  </GridRow>
                )}
              </Grid>

              <ModalBottomButtons
                isLoading={loading}
                submitButton={{
                  disabled: !dirty,
                }}
                closeButton={{
                  callback: () => {
                    resetForm();
                    onClose();
                  },
                }}
                errors={errors.lessonClasses}
              />
            </Form>
          );
        }}
      </Formik>
    );
  },
);
